// Restore-Migration der Block-Parameter (2026-09-29: Eleven v3 → v4).
//
// Sessions in IndexedDB aus der Zeit vor v4 tragen `params.model: 'v3'` und
// `params.v3` (Stability Mode), aber kein `params.v4`. Beim Restore wird das
// Model auf `normalizeTtsModel` gezogen (v3 → v4), fehlende v4-Parameter mit
// dem Default ergänzt und der v3-Teil entfernt. Takes bleiben unangetastet:
// ihr Audio ist gültig, `take.model: 'v3'` beschriftet sie weiter als
// Eleven v3 (Params.txt, Chip-Zusammenfassung).

import type { Block, SessionState, TtsParams } from '@shared/types';
import { TTS_PARAMS_V2_DEFAULT, TTS_PARAMS_V4_DEFAULT } from '@shared/constants';
import { normalizeTtsModel } from '@/lib/modelLock';

type StoredParams = Partial<TtsParams> & { model?: unknown; v3?: unknown };

/** Parameter eines (evtl. alten) Blocks auf das aktuelle Schema bringen.
 *  Bereits aktuelle Parameter kommen per Referenz zurück. */
export function migrateTtsParams(params: TtsParams | undefined): TtsParams {
  const p = (params ?? {}) as StoredParams;
  const model = normalizeTtsModel(p.model) ?? 'v2';
  if (p.model === model && p.v2 && p.v4 && !('v3' in p)) return p as TtsParams;
  return {
    model,
    v2: p.v2 ?? { ...TTS_PARAMS_V2_DEFAULT },
    v4: p.v4 ?? { ...TTS_PARAMS_V4_DEFAULT },
  };
}

function migrateBlocks(blocks: Block[]): Block[] {
  let changed = false;
  const next = blocks.map((b) => {
    const params = migrateTtsParams(b.params);
    if (params === b.params) return b;
    changed = true;
    return { ...b, params };
  });
  return changed ? next : blocks;
}

/** Alle Block-Listen einer Session migrieren (`blocks`, `assetBlocks`,
 *  `freeBlocks`) — gleiche Abdeckung wie `ensureSessionBlockIds`. */
export function migrateSessionModels(s: SessionState): SessionState {
  const assetBlocks: SessionState['assetBlocks'] = {};
  for (const [k, v] of Object.entries(s.assetBlocks ?? {})) assetBlocks[Number(k)] = migrateBlocks(v);
  const freeBlocks: SessionState['freeBlocks'] = {};
  for (const [k, modes] of Object.entries(s.freeBlocks ?? {})) {
    freeBlocks[Number(k)] = {
      tts: migrateBlocks(modes?.tts ?? []),
      sts: migrateBlocks(modes?.sts ?? []),
    };
  }
  return {
    ...s,
    blocks: migrateBlocks(s.blocks ?? []),
    assetBlocks,
    freeBlocks,
  };
}
